import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import _ from 'lodash';
import {
  setDealership,
  resetRefresh,
  resetNav,
  resetAdmin,
  resetSite,
  setCurrentReportTab,
  setReportWorkFlows,
  setCurrentReport,
  setReportTimeRange,
} from '../../actions/apiSetInfo';
import {
  getUser,
  getReportDepartments,
  getTimeDashboardReportDepartments,
} from '../../actions/apiGetInfo';
import {
  change_page,
} from '../../actions/toggleMenu';
import {
  logout,
} from '../../actions/authentication';
import {
  lookupPermission,
  lookupWorkFlowIdByName,
  lookupSortableWorkFlowPropertiesByWorkFlow,
} from '../../actions/lookups';

// Helper function to convert seconds to "dh" format (days and hours only)
const convertSecondsToDH = (seconds) => {
  if (isNaN(seconds)) return 'N/A'; // Only check for NaN
  const days = Math.floor(seconds / (24 * 3600));
  const hours = Math.floor((seconds % (24 * 3600)) / 3600);
  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0 || days === 0) parts.push(`${hours}h`); // Include hours even if zero when days are zero
  return parts.length ? parts.join(' ') : '0h';
};

// Custom utility to get workflow ID (mimicking class component behavior)
const getWorkflowId = (name, work_flows) => {
  const workflow = work_flows.find(w => w.name === name);
  return workflow ? workflow.id : '';
};

const TimeDashboard = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    dealer_settings,
    installation,
    report_department_data,
    departments,
    current_user,
    interval_number,
    work_flows, // Included for ID lookup
  } = useSelector((state) => ({
    dealer_settings: state.settings_site.dealer_settings,
    installation: state.settings_dealerships.installation,
    report_department_data: state.settings_reports.report_department_data,
    departments: state.settings_departments.departments,
    current_user: state.current_user,
    interval_number: state.settings_nav.interval_number,
    work_flows: state.settings_departments.work_flows,
  }), (prev, next) => {
    return prev.dealer_settings === next.dealer_settings &&
           prev.installation === next.installation &&
           prev.report_department_data === next.report_department_data &&
           prev.departments === next.departments &&
           prev.current_user === next.current_user &&
           prev.interval_number === next.interval_number &&
           prev.work_flows === next.work_flows;
  });

  const dealerSettingValue = useCallback((name) => {
    let value = _.sortBy(_.filter(dealer_settings, o => o.key === name && o.dealership === installation?.subdomain), 'id');
    if (value.length === 0) {
      value = _.sortBy(_.filter(dealer_settings, o => o.key === name && (o.dealership === '' || o.dealership === null || o.dealership === undefined)), 'id');
    }
    return value.length > 0 && typeof value[0]?.value !== 'undefined' ? value[0].value : '';
  }, [dealer_settings, installation]);

  const getWorkFlowTime = useCallback((days) => {
    let day = '';
    switch (days) {
      case '7day': day = 'work_flow_7'; break;
      case '14day': day = 'work_flow_14'; break;
      case '30day': day = 'work_flow_30'; break;
      default: day = '';
    }
    return day;
  }, []);

  const getWorkFlowLink = useCallback((days) => {
    let day = 30;
    switch (days) {
      case '7day': day = 7; break;
      case '14day': day = 14; break;
      case '30day': day = 30; break;
      default: day = '';
    }
    return day;
  }, []);

  const onClickReport = useCallback((e) => {
    const report_work_flows = [];
    const work_flow_id = e.target.dataset.id;
    if (work_flow_id) {
      report_work_flows.push(work_flow_id);
      dispatch(setReportWorkFlows(report_work_flows.join()));
      dispatch(setCurrentReport('WorkFlow Averages'));
      dispatch(setCurrentReportTab('Reports'));
      dispatch(setReportTimeRange(e.target.dataset.timerange));
      dispatch(change_page('reports'));
      history.push('/reports');
    } else {
      // console.warn('Invalid work_flow_id:', work_flow_id);
    }
  }, [dispatch, history]);

  // Fetch data on mount or installation change
  useEffect(() => {
    if (installation?.subdomain) {
      dispatch(getTimeDashboardReportDepartments()).catch((error) => {
        // console.error('Failed to fetch report_department_data:', error);
      });
    }
  }, [dispatch, installation]);

  // Move state-dependent logic to useEffect to prevent render-time updates
  useEffect(() => {
    if (!Array.isArray(report_department_data)) {
      // console.warn('report_department_data is not an array:', report_department_data);
    }
  }, [report_department_data]);

  if (dealerSettingValue('timedashboard') !== 'true' || departments.length === 0) return null;

  if (!lookupPermission('page_reports') && current_user.position !== 'admin' && current_user.position !== 'super_admin') return null;

  if (!Array.isArray(report_department_data)) return null; // Guard against invalid data

  const dealership_report_hours = dealerSettingValue('dealership_report_hours');
  const header_rows = [];
  const body_rows = [];
  const footer_rows = [];
  const r = [];
  const t = [];
  const w = [];
  const d = [];
  const vehicle_count = [];
  const report_time = [];

  // console.log('Available report_department_data:', report_department_data); // Debug data

  for (let i = 0; i < 10; i++) {
    r[i] = dealerSettingValue(`timedashboard_${i}`);
    t[i] = dealerSettingValue(`timedashboard_${i}_time`);
    w[i] = getWorkflowId(r[i], work_flows);
    // console.log(`Workflow ${i}: name=${r[i]}, id=${w[i]}, time=${t[i]}`); // Debug workflow lookup

    if (t[i] === 'Current' && r[i] !== 'disabled') {
      const workflow = w[i];
      const workflow_departments = lookupSortableWorkFlowPropertiesByWorkFlow(w[i]);
      if (!Array.isArray(workflow_departments)) {
        // console.warn(`workflow_departments for workflow ${workflow} is not an array:`, workflow_departments);
        continue; // Skip this iteration if invalid
      }

      let vehicle_count_total = 0;
      let report_time_mini = 0;
      workflow_departments.forEach((option) => {
        const dept = departments.find(d => d.id === option.id) || {};
        const dept_subdomain = dept.department_dealerships?.find(d => d.subdomain === installation.subdomain) || false;
        if (dept_subdomain) {
          vehicle_count_total += dept_subdomain.vehicle_count || 0;
          report_time_mini += (dept_subdomain.combined_average || 0) * (dept_subdomain.vehicle_count || 0);
        }
      });

      vehicle_count[i] = vehicle_count_total;
      report_time[i] = vehicle_count_total > 0 && !isNaN(report_time_mini) ? convertSecondsToDH(report_time_mini * 3600) : 'N/A';

      header_rows.push(<th key={`header-${i}`} className="reportHeaderDash" colSpan={2}>{r[i]}<br />({t[i]})</th>);
      body_rows.push(<td key={`body-${workflow}-${i}`} className="bg-light text-dark" colSpan={2} data-id={String(workflow)} data-timerange={30}>{report_time[i]}</td>);
      footer_rows.push(<td key={`footer-${i}`} colSpan={2}>{vehicle_count[i]} vehicles</td>);
    } else if (r[i] !== '' && r[i] !== 'disabled') {
      const workflow = w[i];
      const workflow_time = getWorkFlowTime(t[i]);
      const workflow_link = getWorkFlowLink(t[i]);
      d[i] = report_department_data.find(
        d => d.work_flow_id === workflow && d.report_type === workflow_time && d.dealership === installation.subdomain
      ) || {};
      vehicle_count[i] = d[i].vehicle_count || 0;

      if (dealership_report_hours === 'age-24_7') {
        report_time[i] = d[i].rawtime_24_7 && !isNaN(d[i].rawtime_24_7) ? convertSecondsToDH(d[i].rawtime_24_7 * 3600) : 'N/A';
      } else if (dealership_report_hours === 'age-24_6') {
        report_time[i] = d[i].rawtime_24_6 && !isNaN(d[i].rawtime_24_6) ? convertSecondsToDH(d[i].rawtime_24_6 * 3600) : 'N/A';
      } else {
        report_time[i] = d[i].rawtime_24_5 && !isNaN(d[i].rawtime_24_5) ? convertSecondsToDH(d[i].rawtime_24_5 * 3600) : 'N/A';
      }

      header_rows.push(<th key={`header-${i}`} className="reportHeaderDash" colSpan={2}>{r[i]}<br />({t[i]})</th>);
      body_rows.push(<td key={`body-${workflow}-${i}`} className="bg-light text-dark cursor-pointer" colSpan={2} data-id={String(workflow)} data-timerange={workflow_link} onClick={onClickReport}>{report_time[i]}</td>);
      footer_rows.push(<td key={`footer-${i}`} colSpan={2}>{vehicle_count[i]} vehicles</td>);
    }
  }

  const timedashboard = (
    <table className="time-dashboard-table">
      <thead className="text-center">
        <tr>{header_rows}</tr>
      </thead>
      <tbody className="text-center">
        <tr>{body_rows}</tr>
      </tbody>
      <tfoot className="text-center">
        <tr>{footer_rows}</tr>
      </tfoot>
    </table>
  );

  return <>{timedashboard}</>;
};

export default TimeDashboard;